import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import Dots from 'react-native-dots-pagination';
import Animated from 'react-native-reanimated';
import Carousel from 'react-native-reanimated-carousel';

import { churchToClaim, npoEditedNotClaimed, npoThumbPlaceholder } from '@/assets/images';
import { BLUR_HASH } from '@/constants/layout';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';

import useOnLightRatingPress from '../hooks/useOnLightRatingPress';
import LightRatingButton from './buttons/LightRatingButton';

interface Props {
  thumbnails: string[];
  imageType?: 'churchToClaim' | 'editedNotClaimed' | 'default';
  animatedOpacityStyle: { opacity: number };
  lightRatingValues?: { ranking: number | undefined; name: string };
}

const DetailsHeaderCarousel = ({
  thumbnails,
  imageType = 'default',
  animatedOpacityStyle,
  lightRatingValues
}: Props) => {
  const { width } = useWindowDimensions();
  const { onLightRatingPress } = useOnLightRatingPress();

  const [headerWidth, setHeaderWidth] = useState(width);
  const [activeSlide, setActiveSlide] = useState(0);
  const [carouselWidth, setCarouselWidth] = useState(Platform.OS === 'web' && headerWidth !== 0 ? headerWidth : width);

  const showCarousel = (thumbnails.length ?? 0) > 1 && imageType === 'default';
  const mainImage =
    imageType === 'default'
      ? thumbnails.length > 0
        ? firebaseImage(thumbnails?.at(0), ThumbSize.Large)
        : npoThumbPlaceholder
      : imageType === 'editedNotClaimed'
        ? npoEditedNotClaimed
        : imageType === 'churchToClaim'
          ? churchToClaim
          : '';

  useEffect(() => {
    if ((Platform.OS === 'web' && headerWidth !== 0 ? headerWidth : width) !== 0)
      setCarouselWidth(Platform.OS === 'web' && headerWidth !== 0 ? headerWidth : width);
  }, [headerWidth, width]);

  const onProgressChange = ({ absoluteProgress }: { absoluteProgress: number }) => {
    if (absoluteProgress > thumbnails.length - 1) {
      setActiveSlide(0);
    } else {
      setActiveSlide(Math.round(absoluteProgress));
    }
  };

  return (
    <View
      style={style.detailsHeaderContainer}
      {...(Platform.OS === 'web' && {
        onLayout: (e) => setHeaderWidth(e.nativeEvent.layout.width)
      })}>
      {showCarousel ? (
        <>
          <Carousel
            width={carouselWidth}
            {...(Platform.OS === 'web' && {
              height: carouselWidth
            })}
            data={thumbnails}
            scrollAnimationDuration={1000}
            onProgressChange={(_: number, absoluteProgress: number) => onProgressChange({ absoluteProgress })}
            onConfigurePanGesture={(panGesture) => {
              'worklet';
              panGesture.activeOffsetX([-10, 10]);
            }}
            renderItem={({ index }) => {
              return (
                <Image
                  source={firebaseImage(thumbnails?.at(index), ThumbSize.Large)}
                  style={style.detailImage}
                  contentFit="cover"
                  transition={null}
                  {...(!thumbnails?.at(index) && { placeholder: { blurhash: BLUR_HASH } })}
                />
              );
            }}
          />
          <Animated.View style={[style.dotsIndicator, Platform.OS === 'web' ? { opacity: 1 } : animatedOpacityStyle]}>
            <Dots length={thumbnails.length} active={activeSlide} activeColor="white" passiveColor="#C0C0C0" />
          </Animated.View>
        </>
      ) : (
        <Image
          source={mainImage}
          style={style.detailImage}
          contentFit="cover"
          {...(!thumbnails?.at(0) && { placeholder: { blurhash: BLUR_HASH } })}
          {...(Platform.OS === 'ios' && { transition: 150 })}
        />
      )}
      {lightRatingValues && (
        <Animated.View
          style={[style.lightRatingContainer, Platform.OS === 'web' ? { opacity: 1 } : animatedOpacityStyle]}>
          <LightRatingButton
            variant="medium"
            lightRatingValue={lightRatingValues.ranking ?? 0}
            onPress={() =>
              onLightRatingPress({
                name: lightRatingValues?.name,
                lightRating: lightRatingValues?.ranking ?? null
              })
            }
          />
        </Animated.View>
      )}
    </View>
  );
};

const style = StyleSheet.create({
  detailsHeaderContainer: {
    flex: 1
  },

  dotsIndicator: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0
  },

  lightRatingContainer: {
    position: 'absolute',
    bottom: 14,
    right: 14,
    zIndex: 10,
    elevation: 10
  },

  detailImage: {
    width: '100%',
    height: '100%'
  }
});

export default DetailsHeaderCarousel;
